import React, { useEffect, useRef, useState } from 'react';
import Modal from './Modal.jsx';
import Avatar from './Avatar.jsx';
import { fn, db, logWebEvent } from '../firebase.js';
import { useSession } from '../context/SessionContext.jsx';
import { continuityLineFor, signedInWithLineFor } from '../util/authProvider.js';
import {
  linkApple, linkGoogle, linkEmail,
  signInApple, signInGoogle, signInEmail,
  resetPassword, friendlyAuthError,
} from '../util/authUpgrade.js';
import { isIOS, isAndroid, isInAppBrowser } from '../util/platform.js';
import { usePwaInstallable, promptPwaInstall } from '../util/pwaInstall.js';
import { appStoreUrl } from '../util/appStore.js';

// "Save your account" — links the anonymous session to Apple / Google / Email,
// keeping the same UID so all picks + streak survive. Honest framing — the
// +60 secure-account bonus is real (claimWebFirstSetBonus, WP3-C). Rendered once at
// the AppShell level; opened from the locked Play card or the header pill.
export default function SaveAccountModal() {
  const {
    saveAccountOpen, closeSaveAccount, refreshIdentity, ensureSecuredUserDoc,
    openSaveAccount, openLogin, isAnonymous, authIntent, uid, saveContext, providerId,
  } = useSession();
  const isLogin = authIntent === 'login';
  // Reframed ask when the save was auto-fired from the post-submit H2H beat:
  // "lock in your matchup" + a chained install step in the success screen.
  const h2hLockin = saveContext?.reason === 'h2h_lockin';
  // Save-gate reasons (Tom 2026-07-20): the user tapped Install / Play-with-a-
  // Friend before saving, so this modal is the "save first" warning. onContinue
  // resumes their original action (App Store / friend invite) — from the success
  // screen after a save, or from the skip link if they decline to save.
  const installGate = saveContext?.reason === 'install_gate';
  const friendGate = saveContext?.reason === 'friend_gate';
  // Previously-unhandled reasons (audit R9): the group and follow doors pass
  // these, and the modal fell back to generic copy that dropped the inviter.
  const groupInvite = saveContext?.reason === 'group_invite';
  const predictorFollow = saveContext?.reason === 'predictor_follow';
  const onContinue = typeof saveContext?.onContinue === 'function' ? saveContext.onContinue : null;
  const continueLabel = saveContext?.continueLabel || 'Continue';
  const skipLabel = saveContext?.skipLabel || null;
  const inviter = (saveContext?.inviter || '').trim();
  const [mode, setMode] = useState('choose'); // choose | email
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [awardedAmount, setAwardedAmount] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [usernameError, setUsernameError] = useState(null);
  const usernameRef = useRef(null);
  const [resetSent, setResetSent] = useState(false);
  // iOS Safari steering (audit R5): email starts collapsed behind "More
  // options" so Apple — the provider that actually carries accounts into the
  // app (8/8 installers) — is the visually primary path.
  const [showEmailOption, setShowEmailOption] = useState(false);
  // credential-already-in-use recovery (audit R7): offer the in-sheet switch
  // to login instead of dead-ending on the error text.
  const [offerLoginSwitch, setOfferLoginSwitch] = useState(false);

  // Autofocus the username on open (audit R6) — it's the required first field;
  // waiting for a failed provider tap to focus it taught the ask backwards.
  useEffect(() => {
    if (saveAccountOpen && !isLogin && usernameRef.current) {
      usernameRef.current.focus();
    }
  }, [saveAccountOpen, isLogin]);

  if (!saveAccountOpen) return null;

  const reset = () => {
    setMode('choose');
    setBusy(false);
    setError(null);
    setDone(false);
    setAwardedAmount(0);
    setEmail('');
    setPassword('');
    setShowPassword(false);
    setUsername('');
    setUsernameError(null);
    setResetSent(false);
    setShowEmailOption(false);
    setOfferLoginSwitch(false);
  };
  const close = () => {
    reset();
    closeSaveAccount();
  };

  // Require a username on the create flow so savers land with an identity, not
  // the "Guest" default (Tom 2026-07-21). A NUDGE — focus the field + inline
  // hint — rather than silently no-op'ing the save. Login needs none (the
  // account already has a name). Called at every save-method tap: the providers
  // route through run() below; "Continue with Email" calls it directly so the
  // nudge fires before the email form, not after it.
  const requireUsername = () => {
    if (isLogin) return true;
    if (username.trim().length < 2) {
      setUsernameError('Choose a username first');
      if (usernameRef.current) usernameRef.current.focus();
      return false;
    }
    setUsernameError(null);
    return true;
  };

  const run = async (linkFn) => {
    if (busy) return;
    // The authoritative username gate — every save method (Apple/Google/Email)
    // reaches a save through here, so a blank name can never slip past.
    if (!requireUsername()) return;
    setBusy(true);
    setError(null);
    try {
      await linkFn();
      refreshIdentity();
      // Create the users/{uid} doc NOW (otherwise deferred to the first vote).
      // Without it: the +60 claim below no-ops ("no-user"), the First Group
      // never seeds, and username/group rename fail with not-found. A saver or
      // returning login is a committed account, so this is the right moment.
      try {
        await ensureSecuredUserDoc();
      } catch (e) {
        console.error('[SaveAccount] ensureSecuredUserDoc failed:', e);
      }
      // Persist the chosen username alongside auth. displayName is in the
      // client-write whitelist (mirrors Profile.jsx); the doc exists now
      // (ensureSecuredUserDoc above) and the server displayName-sync trigger
      // sanitizes + fans it out to groups. Create-flow only; best-effort —
      // the account is saved regardless. The name is guaranteed non-blank here
      // (requireUsername gates every save method), so no one lands as "Guest".
      const name = username.trim();
      if (!isLogin && name && uid) {
        try {
          await db.collection('users').doc(uid).update({ displayName: name.slice(0, 32) });
        } catch (e) {
          console.error('[SaveAccount] set username failed:', e);
        }
      }
      // Save (link) banks the +60 secure-account bonus; login is an EXISTING
      // account, so there's no new bonus to claim. Server is idempotent +
      // best-effort: the account is saved regardless of this call.
      if (!isLogin) {
        try {
          const r = await fn.claimWebFirstSetBonus();
          if (r?.data?.awarded) setAwardedAmount(r.data.amount || 60);
        } catch {
          /* bonus best-effort */
        }
      }
      setDone(true);
    } catch (e) {
      console.error('[SaveAccount] link failed:', e);
      const msg = friendlyAuthError(e);
      if (msg) setError(msg); // null = user cancelled, show nothing
      // This credential already belongs to an account (audit R7) — the right
      // move is logging IN, not fighting the link. Offer the switch in-sheet
      // instead of dead-ending on the error text.
      const code = (e?.code || '').toLowerCase();
      if (code.includes('credential-already-in-use') || code.includes('email-already-in-use')) {
        setOfferLoginSwitch(true);
      }
    } finally {
      setBusy(false);
    }
  };

  const submitEmail = (e) => {
    e.preventDefault();
    if (!email.trim() || password.length < 6) {
      setError('Enter an email and a password of at least 6 characters.');
      return;
    }
    run(() => (isLogin ? signInEmail : linkEmail)(email.trim(), password));
  };

  // Forgot-password recovery (login mode). Sends the Firebase reset email. We
  // treat auth/user-not-found as a no-op and show the same confirmation, so the
  // modal never reveals which emails have accounts; only actionable errors
  // (invalid email, rate limit) surface.
  const handleForgotPassword = async () => {
    const addr = email.trim();
    if (!addr) {
      setError('Enter your email above first, then tap “Forgot your password?”.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await resetPassword(addr);
    } catch (e) {
      if (e?.code && e.code !== 'auth/user-not-found') {
        const msg = friendlyAuthError(e);
        if (msg) { setError(msg); setBusy(false); return; }
      }
    }
    setBusy(false);
    setResetSent(true);
    logWebEvent('web_password_reset_sent', {});
  };

  return (
    <Modal
      onClose={close}
      backdropStyle={backdrop}
      sheetStyle={sheet}
      ariaLabel={isLogin ? 'Log in' : 'Save your account'}
    >
      <button onClick={close} aria-label="Close" style={closeBtn}>
        ✕
      </button>

        {/* Gate the success screen on `done` only — keying it on `!isAnonymous`
            too showed a confusing bonus-less "Account saved" if the modal was
            ever opened by an already-secured user (audit B16). */}
        {done ? (
          <div className="ps-center" style={{ padding: '8px 0' }}>
            <p style={{ fontSize: 40, margin: '0 0 8px' }}>✓</p>
            <p style={{ fontWeight: 800, fontSize: 18, margin: '0 0 6px' }}>
              {isLogin ? 'Welcome back' : 'Account saved'}
            </p>
            {/* Item #7 (Tom 2026-07-18): provider FIRST — they JUST picked one,
                so name it while the choice is in their hand. This is the
                cheapest moment to prevent the web→app "you lost my data" class
                (they didn't lose it; a second account is hiding it). Reads the
                live providerId, so it names what they actually tapped.
                See docs/onboarding-experience-2026-07.md §6. */}
            {!isLogin && signedInWithLineFor(providerId) && (
              <p
                style={{
                  color: 'var(--link-gold)',
                  fontWeight: 600,
                  fontSize: 12.5,
                  margin: '0 0 10px',
                  lineHeight: 1.45,
                }}
              >
                {signedInWithLineFor(providerId)}
              </p>
            )}
            {awardedAmount > 0 ? (
              <p
                style={{
                  color: 'var(--gold)',
                  fontWeight: 800,
                  fontSize: 15,
                  margin: '0 0 8px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                <CoinIcon /> +{awardedAmount} coins banked
              </p>
            ) : (
              // Saved before completing a set (the normal save-before-play arc) →
              // the +60 isn't claimable yet; it self-heals after today's six
              // submit (audit B16 + C1).
              !isLogin && (
                <p
                  style={{
                    color: 'var(--gold)',
                    fontWeight: 700,
                    fontSize: 13.5,
                    margin: '0 0 8px',
                    lineHeight: 1.4,
                  }}
                >
                  Play today’s six to earn your bonus coins!
                </p>
              )
            )}
            <p className="ps-muted" style={{ margin: '0 0 18px', lineHeight: 1.5 }}>
              {isLogin
                ? 'You’re signed back in — your picks, streak and coins are restored.'
                : 'Your predictions, streak and games are now safe. Use this sign in method on any device to pick up where you left off.'}
            </p>
            {/* Chained install step — keep the momentum after ANY create-flow
                save (audit R23): one platform-appropriate "get the app" beat at
                the moment of maximum commitment, for every pathway. Excluded
                only where it would compete: install_gate's Continue button IS
                the install, and friend_gate's Continue is the friend invite —
                the loop's more valuable action. */}
            {!isLogin && !installGate && !friendGate && (
              <InstallChainStep sourceReason={saveContext?.reason || 'generic'} />
            )}
            {/* Save-gate: resume the action they came here to do (App Store /
                friend invite) now that the account is safe. */}
            {onContinue ? (
              <button onClick={() => { onContinue(); close(); }} style={primaryBtn}>
                {continueLabel}
              </button>
            ) : (
              <button onClick={close} style={primaryBtn}>
                Done
              </button>
            )}
          </div>
        ) : (
          <>
            {/* The username nudge REPLACES the title (Tom 2026-07-21): on a save
                attempt with no name, "Choose a username first" takes the heading
                slot, in red — the ask and the error share one line of sight. */}
            {/* GAME ON, IN THE SHEET (Tom, 2026-08-11). The avatar-vs-avatar
                reveal used to be a screen of its own that had to be dismissed
                before the user could save. It is the reason they are here, so
                it now sits at the top of the ask itself — one screen, one
                action. */}
            {/* The accept CONFIRMATION lives in the sheet for every door now
                (Tom, 2026-08-11) — "Game on" for a duel, "You're in" for a
                group. Neither gets a screen of its own to dismiss. */}
            {(h2hLockin || groupInvite) && inviter && (
              <p style={{ fontWeight: 900, fontSize: 24, margin: '0 0 12px', textAlign: 'center' }}>
                {h2hLockin ? 'Game on' : 'You’re in'}
              </p>
            )}
            {h2hLockin && inviter && (
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: 14, margin: '0 0 18px' }}>
                <div style={{ textAlign: 'center' }}>
                  <Avatar avatarId={saveContext?.opponentAvatarId ?? null} name={inviter} size={56} frameId={saveContext?.opponentFrameId ?? null} />
                  <div style={{ fontSize: 12, color: 'var(--secondary-text)', marginTop: 6, maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inviter}</div>
                </div>
                <span style={{ color: 'var(--gold)', fontWeight: 800, fontStyle: 'italic', fontSize: 15, marginTop: 20 }}>vs</span>
                <div style={{ textAlign: 'center' }}>
                  <Avatar avatarId={null} name="You" size={56} />
                  <div style={{ fontSize: 12, color: 'var(--secondary-text)', marginTop: 6 }}>You</div>
                </div>
              </div>
            )}
            <p
              role={!isLogin && usernameError ? 'alert' : undefined}
              style={{
                fontWeight: 900,
                fontSize: 20,
                // Centred under the duel, with room before the username box —
                // it lost its body paragraph, so without this the heading sat
                // hard against the input (Tom, 2026-08-11).
                margin: h2hLockin ? '4px 0 16px' : '4px 0 4px',
                textAlign: h2hLockin ? 'center' : undefined,
                color: !isLogin && usernameError ? 'var(--danger)' : undefined,
              }}
            >
              {!isLogin && usernameError
                ? usernameError
                : isLogin
                  ? 'Log in'
                  : h2hLockin
                    ? 'Lock in your matchup'
                    : installGate || friendGate
                      ? 'Save your account first'
                      : 'Save your account'}
            </p>
            {/* OB-2 (Tom 2026-07-16): they JUST picked a provider — say which,
                while the choice is still in their hand. This is the cheapest
                possible moment to prevent the web→app "you lost my data" class
                (they didn't lose it; a second account is hiding it). Reads the
                live providerId, so it names what they actually tapped.
                See docs/onboarding-experience-2026-07.md §6. */}
            {!isLogin && continuityLineFor(providerId) && (
              <p
                style={{
                  color: 'var(--link-gold)',
                  fontWeight: 600,
                  fontSize: 12.5,
                  margin: '0 0 10px',
                  lineHeight: 1.45,
                }}
              >
                {continuityLineFor(providerId)}
              </p>
            )}
            {/* No body copy on the matchup lock-in (Tom, 2026-08-11): the duel
                above already says what this is, and a paragraph between it and
                the username box is just distance to the action. */}
            {!h2hLockin && (
            <p className="ps-muted" style={{ margin: '0 0 18px', lineHeight: 1.5 }}>
              {isLogin
                ? 'Sign back into your saved account — your picks, streak and coins are waiting.'
                  : installGate
                    ? 'Save your account so your picks, streak and coins are waiting for you in the app — it takes a few seconds, then we’ll send you to the App Store. You bank 60 coins for saving.'
                    : friendGate
                      ? 'Save your account so the game you start stays tied to you and follows you into the app — then pick a question to send. You bank 60 coins for saving.'
                      : groupInvite
                        ? `Save your account so your spot in ${inviter || 'the group'} follows you to any device — and bank 60 coins.`
                        : predictorFollow
                          ? `Save your account so your League with ${inviter || 'your Predictor'} counts on any device — and bank 60 coins.`
                          : 'Keep your picks & streak on any device — and in the app — and bank 60 coins. Your picks currently live only in this browser.'}
            </p>
            )}

            {/* In-app webview escape hatch (audit R11): Instagram/Messenger/
                TikTok browsers block OAuth popups, so an Apple/Google tap here
                silently fails — today's only working path is password (0/31
                installed). Say so before they hit it. */}
            {!isLogin && isInAppBrowser() && (
              <div style={loginWarn}>
                Heads up — you’re in an in-app browser, where Apple and Google
                sign-in usually can’t open. Tap <strong>⋯</strong> and choose{' '}
                <strong>Open in Browser</strong> (Safari or Chrome), then save
                your account there. Your picks come with you.
              </div>
            )}

            {/* Login replaces the current guest session, so warn before a brand-
                new player accidentally logs into a fresh account and orphans the
                picks/coins they just earned (audit C2). One tap flips to Save,
                which links in place and keeps everything. */}
            {isLogin && (
              <div style={loginWarn}>
                New here?{' '}
                <strong>Logging in switches to a different account</strong> — picks
                &amp; coins on this guest session won’t carry over.{' '}
                <button type="button" onClick={openSaveAccount} style={loginWarnLink}>
                  Save this session instead
                </button>
              </div>
            )}

            {/* Username — REQUIRED on the create flow (Tom 2026-07-21) so no one
                saves as "Guest". Captured once, applies to whichever provider the
                user picks below; requireUsername() gates every save method. */}
            {!isLogin && (
              <>
                {/* Shake on the nudge (audit R6) — motion draws the eye to the
                    field the red title is talking about. */}
                <style>{`@keyframes psshake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-6px)}40%,80%{transform:translateX(6px)}}`}</style>
                <input
                  ref={usernameRef}
                  type="text"
                  autoComplete="nickname"
                  placeholder="Choose a username"
                  value={username}
                  onChange={(e) => { setUsername(e.target.value); if (usernameError) setUsernameError(null); }}
                  maxLength={32}
                  aria-label="Username"
                  aria-invalid={usernameError ? 'true' : undefined}
                  style={{
                    ...input,
                    marginBottom: 10,
                    // Gold outline + soft ring so the (now required) username field
                    // is the obvious first thing to fill; flips to red on the nudge
                    // (the message itself rides the title line above).
                    border: `1.5px solid ${usernameError ? 'var(--danger)' : 'var(--gold)'}`,
                    boxShadow: usernameError
                      ? '0 0 0 3px color-mix(in srgb, var(--danger) 18%, transparent)'
                      : '0 0 0 3px color-mix(in srgb, var(--gold) 16%, transparent)',
                    animation: usernameError ? 'psshake 0.35s ease' : undefined,
                  }}
                />
              </>
            )}

            {mode === 'choose' ? (
              <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {/* Apple mark as inline SVG — renders white on all platforms
                    (the U+F8FF PUA glyph is tofu off-Apple). Apple leads with
                    a "Fastest" badge (audit R5): installers ran Apple 8/8 vs
                    Google 1/29 vs password 0/31 — the provider chosen here
                    decides whether the account survives the app hand-off. */}
                <ProviderButton
                  label="Continue with Apple"
                  bg="#000"
                  fg="#fff"
                  glyph={<AppleLogo />}
                  badge={isIOS() ? 'Fastest' : null}
                  disabled={busy}
                  onClick={() => run(isLogin ? signInApple : linkApple)}
                />
                <ProviderButton
                  label="Continue with Google"
                  bg="#fff"
                  fg="#1a1a1a"
                  border="1px solid var(--border)"
                  glyph="G"
                  disabled={busy}
                  onClick={() => run(isLogin ? signInGoogle : linkGoogle)}
                />
                {/* On iOS Safari, email hides behind "More options" (audit R5):
                    it's the provider that never survives the install hand-off,
                    so it should be available, not prominent. Everywhere else —
                    and always on login, where an existing password account
                    must stay reachable — it renders as a normal button. */}
                {(!isIOS() || isLogin || showEmailOption) ? (
                  <ProviderButton
                    label="Continue with Email"
                    bg="var(--surface)"
                    fg="var(--primary-text)"
                    glyph="✉"
                    disabled={busy}
                    onClick={() => {
                      // Nudge for a username up front, before the email form —
                      // consistent with the Apple/Google gate in run().
                      if (!requireUsername()) return;
                      setError(null);
                      setMode('email');
                    }}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowEmailOption(true)}
                    style={linkBtn}
                  >
                    More options
                  </button>
                )}
              </div>
              {/* Save-gate escape hatch: let them proceed to their original
                  action without saving, so the gate is a nudge, not a wall. */}
              {skipLabel && onContinue && (
                <button
                  type="button"
                  onClick={() => {
                    logWebEvent('web_save_gate_skipped', { reason: saveContext?.reason || '' });
                    onContinue();
                    close();
                  }}
                  style={{ ...linkBtn, marginTop: 6 }}
                >
                  {skipLabel}
                </button>
              )}
              </>
            ) : (
              <form
                onSubmit={submitEmail}
                style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
              >
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (resetSent) setResetSent(false); }}
                  style={input}
                />
                {/* Show-password toggle (audit R22) — blind phone-keyboard
                    password entry is typo-prone and iOS Safari adds no native
                    reveal. */}
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete={isLogin ? 'current-password' : 'new-password'}
                    placeholder={isLogin ? 'Password' : 'Password (6+ characters)'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{ ...input, paddingRight: 44, boxSizing: 'border-box' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                    style={eyeBtn}
                  >
                    <EyeIcon off={showPassword} />
                  </button>
                </div>
                <button type="submit" disabled={busy} style={primaryBtn}>
                  {busy ? (isLogin ? 'Signing in…' : 'Saving…') : (isLogin ? 'Log in' : 'Save account')}
                </button>
                {isLogin && (
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={busy}
                    style={{ ...linkBtn, fontSize: 13 }}
                  >
                    Forgot your password?
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setError(null);
                    setResetSent(false);
                    setMode('choose');
                  }}
                  style={linkBtn}
                >
                  ← Other options
                </button>
              </form>
            )}

            {busy && (
              <p className="ps-muted ps-center" style={{ fontSize: 13, marginTop: 12 }}>
                Opening sign-in…
              </p>
            )}
            {resetSent && !error && (
              <p
                role="status"
                style={{
                  color: 'var(--gold)',
                  fontWeight: 700,
                  fontSize: 13,
                  marginTop: 12,
                  textAlign: 'center',
                  lineHeight: 1.45,
                }}
              >
                If an account exists for {email.trim()}, a password-reset link is on its way. Tap it to set a new password, then log in.
              </p>
            )}
            {error && (
              <p
                role="alert"
                style={{
                  color: 'var(--danger)',
                  fontWeight: 700,
                  fontSize: 13,
                  marginTop: 12,
                  textAlign: 'center',
                  lineHeight: 1.4,
                }}
              >
                {error}
              </p>
            )}
            {/* credential-already-in-use recovery (audit R7): the tapped
                Apple/Google identity already owns an account — the fix is
                logging into it, so hand them the switch in-sheet. (Their anon
                picks won't merge, which the login warning explains.) */}
            {offerLoginSwitch && !isLogin && (
              <button
                type="button"
                onClick={() => {
                  reset();
                  openLogin();
                }}
                style={{ ...linkBtn, color: 'var(--gold)', fontWeight: 800, marginTop: 6 }}
              >
                Log in to that account instead →
              </button>
            )}
          </>
        )}
    </Modal>
  );
}

function AppleLogo() {
  return (
    <svg
      viewBox="0 0 384 512"
      width="15"
      height="15"
      fill="currentColor"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  );
}

// Gold coin glyph (currentColor → gold from the parent's color), matching the
// CoinIcon used on the Groups tab — replaces the 🪙 emoji which rendered
// inconsistently across platforms.
function CoinIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="9" fill="currentColor" />
      <circle cx="12" cy="12" r="5.5" fill="none" stroke="rgba(0,0,0,0.28)" strokeWidth="1.6" />
    </svg>
  );
}

function ProviderButton({ label, bg, fg, border, glyph, badge, disabled, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        width: '100%',
        padding: '13px 0',
        borderRadius: 'var(--radius-sm)',
        border: border || 'none',
        background: bg,
        color: fg,
        fontWeight: 700,
        fontSize: 15,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {glyph ? (
        <span style={{ fontSize: 16, width: 18, textAlign: 'center' }}>
          {glyph}
        </span>
      ) : null}
      {label}
      {badge ? (
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 800,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: '#1a1a1a',
            background: 'var(--gold)',
            borderRadius: 999,
            padding: '2px 8px',
          }}
        >
          {badge}
        </span>
      ) : null}
    </button>
  );
}

// Open/closed eye for the password reveal toggle (R22), currentColor.
function EyeIcon({ off }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12z" />
      <circle cx="12" cy="12" r="2.7" />
      {off && <line x1="4" y1="20" x2="20" y2="4" />}
    </svg>
  );
}

const backdrop = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  zIndex: 50,
  padding: 0,
};
const sheet = {
  position: 'relative',
  width: '100%',
  maxWidth: 'var(--maxw)',
  background: 'var(--card-bg)',
  borderRadius: '20px 20px 0 0',
  padding: '24px 20px calc(28px + env(safe-area-inset-bottom))',
  boxShadow: '0 -8px 30px rgba(0,0,0,0.18)',
  // Room to grow (Tom, 2026-08-11: "the sheet should be bigger so that 'Game
  // on' can fit above the avatar vs avatar"). It had no height cap at all,
  // which sounds like freedom but means tall content on a short screen runs
  // off the bottom with no way to reach it. A cap plus scroll is what actually
  // makes it bigger: it takes what it needs up to 92vh and stays reachable.
  maxHeight: '92vh',
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
};
const closeBtn = {
  position: 'absolute',
  top: 14,
  right: 14,
  border: 'none',
  background: 'transparent',
  color: 'var(--secondary-text)',
  fontSize: 16,
  lineHeight: 1,
};
const input = {
  width: '100%',
  padding: '13px 14px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--primary-text)',
  fontSize: 15,
};
const primaryBtn = {
  width: '100%',
  padding: '14px 0',
  borderRadius: 'var(--radius-sm)',
  border: 'none',
  background: 'var(--gold)',
  color: '#1a1a1a', // near-black on gold for AA contrast (audit A2)
  fontWeight: 800,
  fontSize: 15,
};
const linkBtn = {
  width: '100%',
  padding: '8px 0',
  border: 'none',
  background: 'transparent',
  color: 'var(--secondary-text)',
  fontSize: 13,
  fontWeight: 600,
};
const loginWarn = {
  margin: '0 0 16px',
  padding: '10px 12px',
  borderRadius: 'var(--radius-sm)',
  background: 'color-mix(in srgb, var(--gold) 10%, transparent)',
  border: '1px solid color-mix(in srgb, var(--gold) 35%, transparent)',
  color: 'var(--primary-text)',
  fontSize: 12.5,
  lineHeight: 1.5,
};
const loginWarnLink = {
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: 'var(--gold)',
  fontWeight: 800,
  fontSize: 12.5,
  cursor: 'pointer',
  textDecoration: 'underline',
};
const eyeBtn = {
  position: 'absolute',
  top: '50%',
  right: 8,
  transform: 'translateY(-50%)',
  width: 32,
  height: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'transparent',
  color: 'var(--secondary-text)',
  cursor: 'pointer',
};

// Post-save install step — platform-appropriate "get the app" so submit → auth
// → install is one unbroken flow, on EVERY create-flow save reason (audit R23).
// iOS → App Store; Android/desktop → the native PWA prompt when the browser
// offers one, else a short Add-to-Home-Screen hint. `sourceReason` rides the
// telemetry so per-pathway install-chain conversion is countable.
function InstallChainStep({ sourceReason = 'generic' }) {
  const installable = usePwaInstallable();
  const ios = isIOS();
  const android = isAndroid();
  const log = (platform) => logWebEvent('web_install_cta_tapped', { platform, source: `save_chain_${sourceReason}` });
  return (
    <div style={installWrap}>
      <p style={{ margin: '0 0 8px', fontWeight: 800, fontSize: 14 }}>Get the app — unlock 12 bonus picks</p>
      {ios ? (
        <a href={appStoreUrl('web_save_chain')} target="_blank" rel="noopener noreferrer" onClick={() => log('ios')} style={installLink}>
          Get the iOS app
        </a>
      ) : android ? (
        installable ? (
          <button onClick={() => { log('android'); promptPwaInstall(); }} style={primaryBtn}>Add to Home Screen</button>
        ) : (
          <p className="ps-muted" style={installHint}>
            Add Predict Six to your Home Screen — open your browser menu, then <strong>Add to Home screen</strong>.
          </p>
        )
      ) : installable ? (
        <button onClick={() => { log('desktop'); promptPwaInstall(); }} style={primaryBtn}>Install Predict Six</button>
      ) : (
        <p className="ps-muted" style={installHint}>
          Open <strong>play.predictsixapp.com</strong> on your phone to play the daily six on the go.
        </p>
      )}
    </div>
  );
}
const installWrap = { margin: '0 0 14px', textAlign: 'center' };
const installLink = {
  ...primaryBtn,
  display: 'block',
  boxSizing: 'border-box',
  textDecoration: 'none',
  textAlign: 'center',
  lineHeight: 1.2,
};
const installHint = { margin: '4px 0 0', fontSize: 12.5, lineHeight: 1.45 };
